import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Globe, Check, X, AlertCircle, ExternalLink, Copy, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomDomainManager = ({ siteId, currentDomain, onDomainUpdated }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleAddDomain = async () => {
    if (!domain.trim()) {
      alert('Please enter a domain name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/website-builder/sites/${siteId}/custom-domain`,
        { domain: domain.trim() }
      );

      setDnsInstructions(response.data.dnsInstructions);
      alert('✅ ' + response.data.message);
      if (onDomainUpdated) onDomainUpdated(domain.trim());
      setDomain('');
    } catch (error) {
      alert('❌ ' + (error.response?.data?.detail || 'Failed to add domain'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm('Are you sure you want to remove this custom domain?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/website-builder/sites/${siteId}/custom-domain`);
      alert('✅ Custom domain removed successfully');
      setDnsInstructions(null);
      if (onDomainUpdated) onDomainUpdated(null);
    } catch (error) {
      alert('❌ Failed to remove domain');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/website-builder/sites/${siteId}/verify-domain`
      );
      alert('✅ ' + response.data.message);
      if (onDomainUpdated) onDomainUpdated(currentDomain, true);
    } catch (error) {
      alert('❌ ' + (error.response?.data?.detail || 'Domain verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Custom Domain
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Domain Status */}
        {currentDomain ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">{currentDomain}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveDomain}
                disabled={loading}
                className="text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-gray-600">Domain configured</span>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full"
              onClick={handleVerifyDomain}
              disabled={loading}
            >
              Verify DNS Configuration
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <Globe className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-4">
              Connect your own domain to make your website more professional
            </p>
            <Button onClick={() => setIsAdding(true)} className="w-full">
              Add Custom Domain
            </Button>
          </div>
        )}

        {/* Add Domain Form */}
        {isAdding && !currentDomain && (
          <div className="space-y-4 border-t pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Your Domain
              </label>
              <Input
                type="text"
                placeholder="example.com or www.example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter domain without http:// or https://
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAddDomain}
                disabled={loading || !domain.trim()}
                className="flex-1"
              >
                {loading ? 'Adding...' : 'Add Domain'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAdding(false);
                  setDomain('');
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* DNS Instructions */}
        {(dnsInstructions || currentDomain) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-2">DNS Configuration Required</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Add this DNS record in your domain registrar's control panel:
                </p>

                <div className="bg-white rounded border border-gray-300 p-3 space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Type:</span>
                    <code className="bg-gray-100 px-2 py-1 rounded">CNAME</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Name:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-2 py-1 rounded">
                        {dnsInstructions?.name || currentDomain || 'www'}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(dnsInstructions?.name || currentDomain || 'www')}
                      >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Value:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-2 py-1 rounded">
                        {dnsInstructions?.value || 'digidreamsolutions.com'}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(dnsInstructions?.value || 'digidreamsolutions.com')}
                      >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-600 space-y-1">
                  <p>📝 <strong>Steps:</strong></p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Log in to your domain registrar (GoDaddy, Namecheap, etc.)</li>
                    <li>Go to DNS settings or DNS management</li>
                    <li>Add a new CNAME record with the values above</li>
                    <li>Wait 5-30 minutes for DNS propagation</li>
                    <li>Click "Verify DNS Configuration" button above</li>
                  </ol>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <a
                    href="https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-to-create-a-cname-record-for-your-domain/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Learn more about DNS configuration
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits */}
        {!currentDomain && !isAdding && (
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Benefits of Custom Domain
            </h4>
            <ul className="text-sm text-gray-600 space-y-1 ml-6 list-disc">
              <li>Professional brand image</li>
              <li>Better SEO rankings</li>
              <li>Increased trust and credibility</li>
              <li>Easy to remember and share</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomDomainManager;
